/**
 * ProtectedRoute component.
 *
 * Redirects unauthenticated users to /login.
 * While the initial token validation is in-flight, shows a loading indicator
 * to prevent a flash redirect for users with a valid stored token.
 *
 * @module components/ProtectedRoute
 */

import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: ReactElement;
  /**
   * Optional role gate. 'employee' restricts to employee-portal accounts;
   * 'employer' restricts to employer accounts (owner/admin). A mismatched
   * user is redirected to their own home rather than shown a 403.
   */
  role?: 'employee' | 'employer';
}

/**
 * Wraps a page component and guards it behind authentication (and optionally a role).
 *
 * Usage:
 *   <Route path="/dashboard" element={<ProtectedRoute role="employer"><Dashboard /></ProtectedRoute>} />
 */
export function ProtectedRoute({ children, role }: ProtectedRouteProps): ReactElement {
  const { token, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Keep employees and employers in their own areas.
  const isEmployee = user?.role === 'employee';
  if (role === 'employee' && !isEmployee) {
    return <Navigate to="/dashboard" replace />;
  }
  if (role === 'employer' && isEmployee) {
    return <Navigate to="/portal" replace />;
  }

  return children;
}
